import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import DashboardService from '../services/dashboardService';

// 防抖函数
const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// 实时数据更新Hook
export const useRealTimeData = (updateInterval = 30000) => {
  const [data, setData] = useState({
    statistics: {},
    recentOrders: [],
    notifications: [],
    systemStatus: {},
    hotProducts: [],
    loading: true,
    lastUpdate: null
  });

  const [autoUpdate, setAutoUpdate] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const intervalRef = useRef(null);
  const loadingRef = useRef(false);
  const consecutiveFailuresRef = useRef(0);

  // 清理函数
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, []);

  // 加载所有数据
  const loadAllData = useCallback(async (silent = false) => {
    if (!mountedRef.current || loadingRef.current) return false;
    loadingRef.current = true;

    try {
      if (!silent) {
        setData(prev => ({ ...prev, loading: true }));
      }
      setError(null);

      const [
        statisticsData,
        ordersData,
        notificationsData,
        systemStatusData,
        hotProductsData
      ] = await Promise.allSettled([
        DashboardService.getStatistics(),
        DashboardService.getRecentOrders(5),
        DashboardService.getNotifications(5),
        DashboardService.getSystemStatus(),
        DashboardService.getHotProducts(8)
      ]);

      if (!mountedRef.current) return false;

      // 处理结果，即使部分失败也要更新成功的数据
      const newData = {
        statistics: statisticsData.status === 'fulfilled' ? statisticsData.value : {},
        recentOrders: ordersData.status === 'fulfilled' ? ordersData.value : [],
        notifications: notificationsData.status === 'fulfilled' ? notificationsData.value : [],
        systemStatus: systemStatusData.status === 'fulfilled' ? systemStatusData.value : {},
        hotProducts: hotProductsData.status === 'fulfilled' ? hotProductsData.value : [],
        loading: false,
        lastUpdate: new Date()
      };

      setData(newData);

      // 检查是否有失败的请求
      const failedRequests = [statisticsData, ordersData, notificationsData, systemStatusData, hotProductsData]
        .filter(result => result.status === 'rejected');
      
      if (failedRequests.length > 0) {
        consecutiveFailuresRef.current += 1;
        if (!silent) {
          console.warn('部分数据加载失败:', failedRequests);
          message.warning('部分数据加载失败，请稍后重试');
        }
        return false;
      }

      consecutiveFailuresRef.current = 0;
      return true;
    } catch (error) {
      if (!mountedRef.current) return false;
      
      console.error('加载数据失败:', error);
      setError(error);
      setData(prev => ({ ...prev, loading: false }));
      
      consecutiveFailuresRef.current += 1;
      if (!silent) {
        message.error('数据加载失败，请稍后重试');
      }

      return false;
    } finally {
      loadingRef.current = false;
    }
  }, []);

  // 手动刷新数据（防抖）
  const refreshData = useCallback(
    debounce(async () => {
      return await loadAllData();
    }, 1000),
    [loadAllData]
  );

  // 切换自动更新
  const toggleAutoUpdate = useCallback(() => {
    setAutoUpdate(prev => {
      const newValue = !prev;
      if (newValue) {
        // 启用自动更新时立即刷新一次
        loadAllData(true);
      }
      return newValue;
    });
  }, [loadAllData]);

  // 初始化数据加载
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // 自动更新定时器；失败时指数退避，避免故障期间持续轰炸接口
  useEffect(() => {
    if (!autoUpdate || !mountedRef.current) {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }

    const scheduleNext = () => {
      const failureMultiplier = Math.min(2 ** consecutiveFailuresRef.current, 10);
      intervalRef.current = setTimeout(async () => {
        if (!mountedRef.current) return;
        await loadAllData(true);
        if (mountedRef.current && autoUpdate) scheduleNext();
      }, updateInterval * failureMultiplier);
    };

    scheduleNext();
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoUpdate, updateInterval, loadAllData]);

  return {
    ...data,
    error,
    refreshData,
    autoUpdate,
    toggleAutoUpdate,
    // 便捷状态
    hasData: Object.keys(data.statistics).length > 0 || data.recentOrders.length > 0,
    isOnline: navigator.onLine
  };
};

// 统计数据Hook
export const useStatistics = () => {
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);

  const loadStatistics = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DashboardService.getStatistics();
      setStatistics(data);
    } catch (error) {
      console.error('加载统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  return { statistics, loading, refresh: loadStatistics };
};

// 最近订单Hook
export const useRecentOrders = (limit = 5) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DashboardService.getRecentOrders(limit);
      setOrders(data);
    } catch (error) {
      console.error('加载最近订单失败:', error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  return { orders, loading, refresh: loadOrders };
};

// 系统通知Hook
export const useNotifications = (limit = 5) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DashboardService.getNotifications(limit);
      setNotifications(data);
    } catch (error) {
      console.error('加载系统通知失败:', error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  return { notifications, loading, refresh: loadNotifications };
};

// 系统状态Hook
export const useSystemStatus = () => {
  const [status, setStatus] = useState({});
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DashboardService.getSystemStatus();
      setStatus(data);
    } catch (error) {
      console.error('加载系统状态失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 30000); // 每30秒更新一次
    return () => clearInterval(interval);
  }, [loadStatus]);

  return { status, loading, refresh: loadStatus };
};

// 热销商品Hook
export const useHotProducts = (limit = 8) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DashboardService.getHotProducts(limit);
      setProducts(data);
    } catch (error) {
      console.error('加载热销商品失败:', error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  return { products, loading, refresh: loadProducts };
};

export default useRealTimeData;
